// Skills.jsx
import { motion } from "framer-motion";
import { useState, useRef, useEffect } from "react"; // ✅ FIX: Import hooks
import {
  FaAws, FaDocker, FaReact, FaNodeJs, FaPython, FaJava, FaWindows,
  FaHtml5, FaCss3Alt, FaJs, FaSlack, FaGithub, FaDropbox, FaUbuntu, FaChartSimple
} from "react-icons/fa6";
import { SiGitlab, SiLangchain, SiHuggingface, SiFastapi, SiOpenai , SiPostgresql, SiMongodb, SiMysql, SiApache } from "react-icons/si";
import { DiRedis,DiDatabase, DiGit  } from "react-icons/di";
import { GiArtificialIntelligence } from "react-icons/gi";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";

// iconMap now includes both the Icon component and a brand color so icons render colorfully.
const iconMap = {
  slack: { Icon: FaSlack, color: "#4A154B" },
  tableau: { Icon: FaChartSimple, color: "#E87722" },
  git: { Icon: DiGit, color: "#F34F29" },
  github: { Icon: FaGithub, color: "#181717" },
  dropbox: { Icon: FaDropbox, color: "#0061FF" },
  aws: { Icon: FaAws, color: "#FF9900" },
  docker: { Icon: FaDocker, color: "#2496ED" },
  gitlab: { Icon: SiGitlab, color: "#FC6D26" },
  ubuntu: { Icon: FaUbuntu, color: "#E95420" },
  windows: { Icon: FaWindows, color: "#0078D6" },
  apache: { Icon: SiApache, color: "#D22128" },
  mysql: { Icon: SiMysql, color: "#4479A1" },
  openai: { Icon: SiOpenai, color: "#412991" },
  langchain: { Icon: SiLangchain, color: "#0EA5A4" },
  huggingface: { Icon: SiHuggingface, color: "#FF6A00" },
  react: { Icon: FaReact, color: "#61DAFB" },
  nodejs: { Icon: FaNodeJs, color: "#339933" },
  python: { Icon: FaPython, color: "#3776AB" },
  java: { Icon: FaJava, color: "#007396" },
  html5: { Icon: FaHtml5, color: "#E34F26" },
  css3: { Icon: FaCss3Alt, color: "#1572B6" },
  javascript: { Icon: FaJs, color: "#F7DF1E" },
  fastapi: { Icon: SiFastapi, color: "#009688" },
  database: { Icon: DiDatabase, color: "#6C8EBF" },
  postgresql: { Icon: SiPostgresql, color: "#336791" },
  mongodb: { Icon: SiMongodb, color: "#47A248" },
  redis: { Icon: DiRedis, color: "#D82C20" },
  ai: { Icon: GiArtificialIntelligence, color: "#7C3AED" },
};
const iconsRow1 = [ "slack", "tableau", "git", "github", "dropbox", "aws", "gitlab", "docker","ubuntu", "windows", "mysql", "apache", "openai", "langchain" ];
const iconsRow2 = [ "react", "nodejs", "python", "java", "html5", "css3", "javascript", "fastapi", "database", "postgresql", "mongodb", "redis", "ai" ];


function IconBox({ name, i, tooltipGradient = "to-r, #6EE7B7, #3B82F6" }) {
  const mapEntry = iconMap[name] || {};
  const Icon = mapEntry.Icon || (() => null);
  const brandColor = mapEntry.color || "#111827"; // fallback dark

  // create a semi-transparent bg color for tooltip
  const tooltipBg = (hex, alpha = 0.95) => {
    // simple hex to rgba converter for 6-digit hex
    try {
      const h = hex.replace('#', '');
      const r = parseInt(h.substring(0, 2), 16);
      const g = parseInt(h.substring(2, 4), 16);
      const b = parseInt(h.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    } catch (e) {
      return hex;
    }
  };

  return (
    <motion.div
      className="group flex-none shrink-0 w-20 h-20 md:w-24 md:h-24 aspect-square rounded-2xl border border-gray-200 bg-white shadow-sm hover:shadow-xl hover:border-transparent transition-all duration-300 ease-out cursor-pointer relative items-center justify-center inline-flex overflow-visible"
      whileHover={{ scale: 1.06 }}
      data-tooltip-id={`tip-${name}-${i}`}
    >
      <motion.span className="inline-flex" whileHover={{ scale: 1.18, rotate: [0, -6, 6, 0] }} transition={{ duration: 0.35 }}>
        <Icon size={44} color={brandColor} className="md:size-[48]" />
      </motion.span>
      <Tooltip
        id={`tip-${name}-${i}`}
        place="top"
        strategy="fixed"
        style={{
          background: tooltipBg(brandColor, 0.95),
          color: "#fff",
          borderRadius: "10px",
          fontSize: "0.85rem",
          padding: "6px 10px",
          zIndex: 9999,
          boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
        }}
      >
        {name.toUpperCase()}
      </Tooltip>
    </motion.div>
  );
}

export default function Skills() {
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);
  // ✅ FIX: Use state for dynamic duration, defaulting to your original value.
  const [duration1, setDuration1] = useState(50);
  const [duration2, setDuration2] = useState(50);

  // ✅ FIX: Calculate duration based on width to ensure constant speed
  useEffect(() => {
    const calculateDurations = () => {
      if (row1Ref.current && row2Ref.current) {
        const speed = 75; // pixels per second
        // The width of one set of icons is half the scrollable width
        const width1 = row1Ref.current.scrollWidth / 2;
        const width2 = row2Ref.current.scrollWidth / 2;

        setDuration1(width1 / speed);
        setDuration2(width2 / speed);
      }
    };

    calculateDurations();

    // Recalculate on window resize
    window.addEventListener('resize', calculateDurations);
    return () => window.removeEventListener('resize', calculateDurations);
  }, []);

  return (
    <div className="bg-white py-20" style={{
      // background: "linear-gradient(bottom to, #FFFFFF 56%, #AC2E4F 21%)"
    }}>
      <div className="max-w-6xl mx-auto px-6 text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-4 text-sky-950 h-color">
          Tech Tools
        </h1>
        <p className="text-sky-900 mb-12">
          We streamline your workflow by integrating the required tools seamlessly with your idea, <br></br>
          enabling you with latest and powerful solutions for your ideas.
        </p>

        {/* Row 1 -> moves left */}
        <div className="relative overflow-hidden py-10">
          <motion.div
            ref={row1Ref}
            className="flex gap-6 md:gap-8 will-change-transform"
            animate={{ x: ["0%", "-100%"] }}   // ✅ smooth continuous scroll
            transition={{ repeat: Infinity, ease: "linear", duration: duration1 }}
          >
            {[...iconsRow1, ...iconsRow1].map((n, idx) => (
              <IconBox key={`r1-${idx}`} name={n} i={idx} />
            ))}
          </motion.div>
        </div>

        {/* Row 2 -> moves right */}
        <div className="relative overflow-hidden py-10">
          <motion.div
            ref={row2Ref}
            className="flex gap-6 md:gap-8 will-change-transform"
            animate={{ x: ["-100%", "0%"] }}   // ✅ smooth continuous scroll (rightwards)
            transition={{ repeat: Infinity, ease: "linear", duration: duration2 }}
          >
            {[...iconsRow2, ...iconsRow2].map((n, idx) => (
              <IconBox key={`r2-${idx}`} name={n} i={idx} tooltipGradient="to-r, #F59E0B, #EF4444" />
            ))}
          </motion.div>
        </div>
      </div>
    </div>
  );
}